import * as React from 'react'
import { ReactNode } from 'react'
import { Button, Modal } from 'react-bootstrap'

import ArticlePanel from '../../ArticlePanel'
import { AiVariationContext, HideModalCallback } from '../aiVariationTypes'
import { useManageAiVariationsModal } from './useManageAiVariationsModal'

import './ManageAiVariationsModal.scss'
import { ManageAiVariationsLoadingSpinner } from './manageAiVariationsLoadingSpinner/ManageAiVariationsLoadingSpinner'
import ManageAiVariationsTokenModal from './manageAiVariationsTokenModal/ManageAiVariationsTokenModal'
import Icon, { IconType } from '../../../../../../common/components/icon/Icon'

const ManageAiVariationsModal: React.FunctionComponent<AiVariationContext & HideModalCallback> = (props) => {
	const {
		campaignId,
		campaignName,

		viewingWithToken,
		loading,
		generatingMoreSuggestions,
		error,

		aiSuggestionArticle,
		updateVariationField,
		handleApproveOrDisapproveAiSuggestion,

		numberOfApprovedOrDismissedSuggestions,
		numberOfApprovedArticles,
		numberOfMissingArticles,

		handleLoadMore,

		showTokenModal,
		handleOpenTokenModal,
		handleHideTokenModal,

		handleHideModalAndRefreshParentIfRequired,

		duplicateVariation,
		removeVariation,
		addVariation,
	} = useManageAiVariationsModal(props)

	const renderAiSuggestionsUi = () => (
		<>
			{loading && (
				<div className="centered">
					<Icon icon={IconType.Sync} spin={true} />{' '}
					{generatingMoreSuggestions
						? 'AI is generating variations. This may take a while. Please wait a moment ...'
						: 'Please wait ...'}
					{generatingMoreSuggestions && (
						<ManageAiVariationsLoadingSpinner existingVariations={aiSuggestionArticle?.variations} />
					)}
				</div>
			)}
			{error && (
				<div className="error">An error occurred while generating variations. Try again a bit later.</div>
			)}
			{!loading && aiSuggestionArticle?.variations?.length === 0 && (
				<div className="centered">No unapproved variations exist.</div>
			)}
			{!loading && aiSuggestionArticle?.variations && (
				<ArticlePanel
					article={aiSuggestionArticle}
					campaignId={campaignId}
					updateVariationField={updateVariationField}
					duplicateVariation={duplicateVariation}
					removeVariation={removeVariation}
					addVariation={addVariation}
					managingAiSuggestions={true}
					approveOrDisapproveAiSuggestion={handleApproveOrDisapproveAiSuggestion}
					adFormat={props.adFormat}
				/>
			)}
		</>
	)

	const renderHeading = () => (
		<div className="AiVariationsHeading">
			<h1>{campaignName}</h1>
			<h2>Readpeak AI Ad Variations for Ad Group {props.adGroupNumber}</h2>
		</div>
	)

	const renderMoreButton = () => (
		<div className="moreButton">
			<Button bsStyle="primary" bsSize="small" onClick={handleLoadMore}>
				Shuffle {numberOfMissingArticles || ''} more AI ad{plural(numberOfMissingArticles)}
			</Button>
			<br />
			{numberOfApprovedArticles ? (
				<>
					Keep the ({numberOfApprovedArticles}) approved ad variation{plural(numberOfApprovedArticles)}
					<br />
					and shuffle {numberOfMissingArticles > 1 ? 'more' : 'one'} new ad{plural(numberOfMissingArticles)}
				</>
			) : (
				<>Shuffle more new ads</>
			)}
		</div>
	)

	const renderCloseButton = (buttonText: string, infoTextElement: ReactNode, approveAndDismiss: boolean) => (
		<div className="closeButton">
			<Button
				bsStyle="primary"
				bsSize="small"
				onClick={() => handleHideModalAndRefreshParentIfRequired(approveAndDismiss)}
			>
				{buttonText}
			</Button>
			{numberOfApprovedOrDismissedSuggestions > 0 && (
				<>
					<br />
					{infoTextElement}
				</>
			)}
		</div>
	)

	const plural = (value: number) => (value !== 1 ? 's' : '')

	return viewingWithToken ? (
		<div className="ManageAiVariationsFakeModal">
			<div className="headerContainerForTokenUser">
				{renderHeading()}
				<div className="AiVariationsButtons">
					{renderMoreButton()}
					{!!aiSuggestionArticle?.variations?.length &&
						renderCloseButton(
							'Save',
							<>
								Approve and dismiss
								<br />
								{numberOfApprovedOrDismissedSuggestions} selected item
								{plural(numberOfApprovedOrDismissedSuggestions)}
							</>,
							true,
						)}
				</div>
			</div>
			{renderAiSuggestionsUi()}
		</div>
	) : (
		<>
			<Modal
				className="ManageAiVariationsModal"
				show={true}
				onHide={handleHideModalAndRefreshParentIfRequired}
				bsSize="large"
			>
				<Modal.Header>
					<div className="row">
						<div className="ManageAiVariationsModalHeader col-lg-12 col-md-12 col-sm-12 col-xs-12">
							{renderHeading()}
							<div className="AiVariationsButtons">
								<div className="tokenButton">
									<Button bsStyle="primary" bsSize="small" onClick={handleOpenTokenModal}>
										External approval
									</Button>
									<br />
									Send AI ads for
									<br />
									external approval
								</div>
								{renderMoreButton()}
								{numberOfApprovedOrDismissedSuggestions > 0 &&
									renderCloseButton(
										'Save',
										<>
											Approve and dismiss
											<br />
											{numberOfApprovedOrDismissedSuggestions} selected item
											{plural(numberOfApprovedOrDismissedSuggestions)} and close
										</>,
										true,
									)}
								{renderCloseButton('Close', <></>, false)}
							</div>
						</div>
					</div>
				</Modal.Header>
				<Modal.Body>{renderAiSuggestionsUi()}</Modal.Body>
			</Modal>
			{showTokenModal && (
				<ManageAiVariationsTokenModal
					campaignId={props.campaignId}
					adGroupId={props.adGroupId}
					adGroupNumber={props.adGroupNumber}
					advertiserName={props.advertiserName}
					hideModal={handleHideTokenModal}
					adFormat={props.adFormat}
				></ManageAiVariationsTokenModal>
			)}
		</>
	)
}

export default ManageAiVariationsModal
